import React, { useState, useEffect } from "react";
import {
  FaLock,
  FaShieldAlt,
  FaKey,
  FaCheck,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaFileAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import {
  get2FAStatusApi,
  send2FAEnableCodeApi,
  disable2FAApi,
  changePasswordApi,
} from "../../../apis/payments.api";
import { useDarkMode } from "../../../hooks/useDarkMode";

const SecuritySettings = ({ onToggle2FA }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    location?.state?.success || null,
  );

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [isFirebaseUser, setIsFirebaseUser] = useState(false);

  useEffect(() => {
    fetchTwoFAStatus();

    // Check if user is Firebase authenticated
    // Since ALL your users are Firebase authenticated, we should default to true
    // or check for specific Firebase indicators
    try {
      const acc = JSON.parse(localStorage.getItem("account") || "{}");
      const token = localStorage.getItem("token");

      // If there's a Firebase token or uid, user is Firebase authenticated
      // For your app, all users come through Firebase, so default to true
      const hasFirebaseAuth = !!(acc.uid || token);

      setIsFirebaseUser(hasFirebaseAuth || true); // Default to true since you use Firebase
    } catch (err) {
      setIsFirebaseUser(true); // Default to Firebase user
    }
  }, [location?.state?.success]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchTwoFAStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await get2FAStatusApi();
      setTwoFactorEnabled(res.data?.two_factor_enabled || false);
    } catch (err) {
      setError("Failed to load 2FA status");
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setActionLoading(true);
      setError(null);

      // Send OTP for 2FA setup
      const res = await send2FAEnableCodeApi();
      // console.log("OTP sent for 2FA setup:", res.data);

      // Get user email for display
      let email = "";
      try {
        const acc = JSON.parse(localStorage.getItem("account") || "{}");
        email = acc.email || "";
        if (!email) {
          const usr = JSON.parse(localStorage.getItem("user") || "{}");
          email = usr.email || "";
        }
      } catch {}

      // Store the enable flag in sessionStorage
      sessionStorage.setItem("enable2FA", "true");

      // Redirect to 2FA verification
      navigate("/login-2fa", {
        state: {
          email,
          mode: "enable-2fa", // Indicate this is for enabling 2FA
        },
      });
    } catch (err) {
      // console.error("Failed to enable 2FA:", err);
      setError(err?.response?.data?.error || "Failed to send 2FA code");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (
      window.confirm(
        "Are you sure you want to disable two-factor authentication?",
      )
    ) {
      try {
        setActionLoading(true);
        setError(null);

        await disable2FAApi();
        setTwoFactorEnabled(false);
        setSuccessMessage("Two-factor authentication has been disabled");

        if (onToggle2FA) {
          onToggle2FA(false);
        }

        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        // console.error("Failed to disable 2FA:", err);
        setError("Failed to disable 2FA");
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Validation
    if (!passwordData.new_password || !passwordData.confirm_password) {
      setPasswordError("New password and confirmation are required");
      return;
    }

    // For non-Firebase users, require current password
    if (!isFirebaseUser && !passwordData.current_password) {
      setPasswordError("Current password is required");
      return;
    }

    if (passwordData.new_password.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordData.current_password === passwordData.new_password) {
      setPasswordError("New password must be different from current password");
      return;
    }

    try {
      setPasswordLoading(true);

      // Trim whitespace from passwords
      const trimmedData = {
        current_password: passwordData.current_password.trim(),
        new_password: passwordData.new_password.trim(),
        confirm_password: passwordData.confirm_password.trim(),
      };

      // console.log("[PASSWORD CHANGE] Sending request:", {
      //   current_password: trimmedData.current_password ? "***" : "(empty)",
      //   new_password: "***",
      //   confirm_password: "***",
      //   isFirebaseUser
      // });

      // For Firebase users without password, send a placeholder
      const requestData = {
        ...trimmedData,
        current_password:
          trimmedData.current_password || "firebase-initial-setup",
      };

      const response = await changePasswordApi(requestData);

      setSuccessMessage(
        "Password set successfully! You can now login using your email and this password (don't use Google Sign-In).",
      );
      setShowPasswordModal(false);
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });

      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err) {
      // Handle different error formats
      let errorMessage = "Failed to change password";
      if (err?.error) {
        errorMessage = err.error;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-200">
        Loading security settings...
      </div>
    );
  }

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`space-y-6 overflow-x-hidden w-full max-w-full ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
    >
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-200 px-4 py-3 rounded">
          ✓ {successMessage}
        </div>
      )}

      {/* Two-Factor Authentication */}
      <div
        className={`border rounded-lg p-6 w-full max-w-full ${isDarkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white"}`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between w-full">
          <div className="flex items-start gap-4 min-w-0">
            <div
              className={`p-3 hidden sm:block  ${isDarkMode ? "bg-blue-200" : "bg-blue-100"} rounded-lg`}
            >
              <FaShieldAlt
                className={`w-6 h-6  ${isDarkMode ? "text-blue-700" : "text-blue-700"}`}
              />
            </div>
            <div>
              <h3
                className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"} flex items-center gap-2`}
              >
                <FaKey className="w-4 h-4" />
                Two-Factor Authentication
              </h3>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {twoFactorEnabled
                  ? "Your account is protected with 2FA. You'll receive a code via email when signing in."
                  : "You'll need to enter a code sent to your email when signing in."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {twoFactorEnabled ? (
              <span className="flex items-center gap-2 text-green-600 font-semibold">
                <FaCheck className="w-4 h-4" />
                Enabled
              </span>
            ) : (
              <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-semibold">
                <FaTimes className="w-4 h-4" />
                Disabled
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 w-full max-w-full">
          {!twoFactorEnabled ? (
            <button
              onClick={handleEnable2FA}
              disabled={actionLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
            >
              {actionLoading ? "Setting up..." : "Enable 2FA"}
            </button>
          ) : (
            <button
              onClick={handleDisable2FA}
              disabled={actionLoading}
              className={`px-6 py-2   ${isDarkMode ? "hover:bg-red-800/60 hover:text-red-200 text-red-500 bg-red-100 " : "bg-red-200 text-red-600 hover:bg-red-900/40"} rounded-lg  disabled:opacity-50 disabled:cursor-not-allowed font-medium transition`}
            >
              {actionLoading ? "Disabling..." : "Disable 2FA"}
            </button>
          )}
        </div>
      </div>

      {/* Password Security */}
      <div
        className={`border rounded-lg p-6 w-full max-w-full ${isDarkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white"}`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between w-full">
          <div className="flex items-start gap-4 min-w-0">
            <div className="p-3 hidden sm:block  bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <FaLock
                className={`w-6 h-6 ${isDarkMode ? "text-purple-300" : "text-purple-600"}`}
              />
            </div>
            <div>
              <h3
                className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"} flex items-center gap-2`}
              >
                Change Password
              </h3>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Update your password regularly to keep your account secure.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:gap-4 w-full max-w-full">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="px-6 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 font-medium transition"
          >
            Change Password
          </button>
        </div>
      </div>

      {/* Active Sessions Info */}
      <div
        className={`${isDarkMode ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"} rounded-lg p-4`}
      >
        <p
          className={`text-sm ${isDarkMode ? "text-blue-200" : "text-blue-800"}`}
        >
          <strong>Tip:</strong> Check your "Devices" section to view active
          sessions and manage your connected devices.
        </p>
      </div>

      {/* Legal & Privacy */}
      <div
        className={`border rounded-lg px-2 py-4 sm:p-6 ${isDarkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white"}`}
      >
        <div className="flex items-start gap-4 mb-4">
          <div
            className={`p-3 ${isDarkMode ? "bg-green-900/40" : "bg-green-100"} rounded-lg`}
          >
            <FaFileAlt
              className={`w-6 h-6 ${isDarkMode ? "text-green-300" : "text-green-600"}`}
            />
          </div>
          <div className="flex-1">
            <h3
              className={`font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
            >
              Legal & Privacy
            </h3>
            <p
              className={`text-sm mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              Review our policies and terms of service
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate("/dashboard/account-info/privacy-policy")}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition ${
              isDarkMode
                ? "border-gray-700 hover:bg-gray-700/50 bg-gray-900/30"
                : "border-gray-200 hover:bg-gray-50 bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <FaShieldAlt
                className={`w-5 h-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
              />
              <div className="text-left">
                <p
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Privacy Policy
                </p>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Learn how we protect your data
                </p>
              </div>
            </div>
            <FaExternalLinkAlt
              className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          </button>

          <button
            onClick={() => navigate("/dashboard/account-info/terms-conditions")}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition ${
              isDarkMode
                ? "border-gray-700 hover:bg-gray-700/50 bg-gray-900/30"
                : "border-gray-200 hover:bg-gray-50 bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <FaFileAlt
                className={`w-5 h-5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
              />
              <div className="text-left">
                <p
                  className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Terms & Conditions
                </p>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Read our terms of service
                </p>
              </div>
            </div>
            <FaExternalLinkAlt
              className={`w-4 h-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          </button>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-100 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <FaLock className="text-purple-600 dark:text-purple-300" />
                  Change Password
                </h2>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError(null);
                    setPasswordData({
                      current_password: "",
                      new_password: "",
                      confirm_password: "",
                    });
                  }}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl"
                >
                  ×
                </button>
              </div>

              {passwordError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
                  {passwordError}
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-4">
                {/* Note for Firebase users */}
                {isFirebaseUser && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-600 rounded-lg p-4 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-600 dark:text-yellow-300 text-xl">
                        ⚠️
                      </span>
                      <div>
                        <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                          Google Sign-In Detected
                        </p>
                        <p className="text-yellow-700 dark:text-yellow-100">
                          You're signed in with Google.{" "}
                          <strong>Leave "Current Password" field EMPTY</strong>{" "}
                          and just enter your new password below to set up
                          password login.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Current Password{" "}
                    {isFirebaseUser && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        (optional for Google users)
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          current_password: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={
                        isFirebaseUser
                          ? "Leave empty to set new password"
                          : "Enter current password"
                      }
                      required={!isFirebaseUser}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("current")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          new_password: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Enter new password (min 8 characters)"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("new")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirm_password: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("confirm")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordError(null);
                      setPasswordData({
                        current_password: "",
                        new_password: "",
                        confirm_password: "",
                      });
                    }}
                    className="flex-1 px-6 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
                  >
                    {passwordLoading ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div></>
  );
};

export default SecuritySettings;
